import { useState, useEffect, useRef } from 'react';

interface GenerationProgressProps {
  /** Title shown above the progress bar */
  title: string;
  /** Message shown below the title */
  message: string;
  /** Estimated total duration in seconds (default: 90) */
  estimatedDuration?: number;
}

/**
 * Animated progress bar for AI generation.
 * Uses an easing curve: fast at first, slows near the end, never reaches 100% until unmount.
 */
export function GenerationProgress({
  title,
  message,
  estimatedDuration = 90,
}: GenerationProgressProps) {
  const [progress, setProgress] = useState(0);
  const startTimeRef = useRef(Date.now());
  const rafRef = useRef<number>();

  useEffect(() => {
    startTimeRef.current = Date.now();
    setProgress(0);

    const tick = () => {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      const ratio = Math.min(elapsed / estimatedDuration, 1);

      // Easing: fast start, slow finish. Asymptotically approaches 95%.
      // Formula: 95 * (1 - e^(-3 * ratio))
      const value = 95 * (1 - Math.exp(-3 * ratio));
      setProgress(Math.round(value));

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [estimatedDuration]);

  return (
    <div className="flex flex-col gap-[16px] items-center justify-center py-[40px]">
      <h2 className="font-manrope font-bold text-[32px] leading-[40px] text-[#0d0d0d] text-center">
        {title}
      </h2>
      <p className="font-segoe text-[16px] leading-[24px] text-[#565858] text-center">
        {message}
      </p>

      {/* Progress bar container */}
      <div className="w-full max-w-[320px] flex flex-col gap-[8px]">
        <div className="w-full h-[6px] bg-[#eeeeee] rounded-full overflow-hidden">
          <div
            className="h-full bg-[#2197f1] rounded-full transition-[width] duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="font-segoe text-[14px] leading-[20px] text-[#969696] text-center">
          {progress}%
        </p>
      </div>
    </div>
  );
}
